import React, { useEffect, useState, useRef } from 'react';
import './dash.css';
import profilePictureContImage from '../Images/defprofile.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClockRotateLeft,
  faEnvelope,
  faPhone,
  faShieldHalved,
  faUserTag
} from '@fortawesome/free-solid-svg-icons';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, getDocs, query, where, addDoc, orderBy, serverTimestamp } from "@firebase/firestore";
import { app } from '../firebase.js';
import { BRAND_NAME } from '../branding';
import { useNavigate } from 'react-router-dom';

function ProfileComp() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUser, setAuthUser] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const db = getFirestore(app);
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
      if (!user) {
        setLoading(false);
        return;
      }
      let adminName = user.displayName || user.email || 'Admin';
      let foundProfile = null;
      const collectionsToTry = ["Admin", "Admins"];
      const fieldCandidates = ["userID", "userId", "uid"];

      for (const colName of collectionsToTry) {
        for (const field of fieldCandidates) {
          const q = query(collection(db, colName), where(field, "==", user.uid));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docData = snap.docs[0].data();
            adminName = docData?.userName || adminName;
            foundProfile = { id: snap.docs[0].id, ...docData };
            break;
          }
        }
        if (foundProfile) break;
      }

      if (!foundProfile && user.email) {
        for (const colName of collectionsToTry) {
          const q = query(collection(db, colName), where("userEmail", "==", user.email));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docData = snap.docs[0].data();
            adminName = docData?.userName || adminName;
            foundProfile = { id: snap.docs[0].id, ...docData };
            break;
          }
        }
      }

      if (foundProfile) {
        setProfile(foundProfile);
      }

      const logsQuery = query(
        collection(db, "AdminActivity"),
        where("adminId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const logsSnap = await getDocs(logsQuery);
      const logs = [];
      logsSnap.forEach((doc) => {
        logs.push({ id: doc.id, ...doc.data() });
      });
      setActivityLogs(logs);

      if (!hasLoggedRef.current) {
        await addDoc(collection(db, "AdminActivity"), {
          adminId: user.uid,
          adminName,
          action: "Viewed Profile",
          details: "Opened profile page",
          createdAt: serverTimestamp()
        });
        hasLoggedRef.current = true;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  if (loading) {
    return <div className="profileComp"><p>Loading profile...</p></div>;
  }

  if (!profile) {
    return <div className="profileComp"><p>No profile data found.</p></div>;
  }

  const profilePic = profile.profilePic || profilePictureContImage;
  const name = profile.userName || 'User';
  const email = profile.userEmail || authUser?.email || 'N/A';
  const phone = profile.phone || 'N/A';
  const adminType = profile.adminType || 'Admin';
  const totalActivities = activityLogs.length;
  const profileInitial = String(name || 'U').trim().charAt(0).toUpperCase();
  
  const formatLogDate = (value) => {
    if (!value) return 'N/A';
    if (value?.toDate) {
      return value.toDate().toLocaleString();
    }
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString();
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString();
  };

  const getPhoneLogTarget = (log) => {
    if (log?.studentId) {
      return { studentId: log.studentId, studentName: log.studentName || 'student' };
    }
    const details = log?.details || '';
    const match = details.match(/^Viewed phone for (.+) \((.+)\)$/);
    if (!match) return null;
    return { studentName: match[1] || 'student', studentId: match[2] || '' };
  };

  const infoCards = [
    {
      icon: faEnvelope,
      label: 'Email address',
      value: email
    },
    {
      icon: faPhone,
      label: 'Phone number',
      value: phone
    },
    {
      icon: faUserTag,
      label: 'Admin type',
      value: adminType
    }
  ];

  return (
    <div className="profileComp profilePage">
      <div className="profileHeroCard">
        <div className="profileHeroBackdrop"></div>
        <div className="profileHeroContent">
          <div className="profileIdentityBlock">
            <div className="profilePictureCont profilePictureCont--modern">
              <img src={profilePic} alt={name} />
              {!profile.profilePic && <span className="profilePictureFallback">{profileInitial}</span>}
            </div>
            <div className="profileIdentityText">
              <p className="profileEyebrow">Admin profile</p>
              <h3>{name}</h3>
              <div className="profileMetaChips">
                <span className="profileMetaChip">
                  <FontAwesomeIcon icon={faShieldHalved} />
                  {adminType}
                </span>
                <span className="profileMetaChip">
                  <FontAwesomeIcon icon={faEnvelope} />
                  {email}
                </span>
              </div>
              <p className="profileHeroBlurb">{profile.aboutMe || `Administrator account for ${BRAND_NAME}.`}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="profileBody profileBody--modern">
        <div className="profileSidebar">
          <div className="profilePanelCard">
            <div className="profileSectionHeader">
              <div>
                <p className="profileSectionEyebrow">Overview</p>
                <h6>Account details</h6>
              </div>
            </div>
            <div className="profileInfoGrid">
              {infoCards.map((item) => (
                <div key={item.label} className="profileInfoCard">
                  <div className="roundProfileIconCont roundProfileIconCont--modern">
                    <FontAwesomeIcon icon={item.icon} className="profileOverviewIcon" />
                  </div>
                  <div>
                    <p className="profileContactCardTitle">{item.label}</p>
                    <strong>{item.value}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="profileOverview profileOverview--modern">
          <div className="profileSectionHeader profileSectionHeader--logs">
            <div>
              <p className="profileSectionEyebrow">History</p>
              <h6>User Activity Logs</h6>
            </div>
            <div className="profileLogsBadge">
              <FontAwesomeIcon icon={faClockRotateLeft} />
              <span>{totalActivities} entries</span>
            </div>
          </div>

          <div className="table-responsive activityLogTable activityLogTable--modern">
            <table className="table profileActivityTable">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.length === 0 && (
                  <tr>
                    <td colSpan="3" className="profileActivityEmpty">No activity logs yet.</td>
                  </tr>
                )}
                {activityLogs.map((log) => {
                  const phoneTarget = log.action === 'Viewed student phone number'
                    ? getPhoneLogTarget(log)
                    : null;
                  return (
                    <tr key={log.id}>
                      <td className="profileActivityDate">{formatLogDate(log.createdAt)}</td>
                      <td>
                        <span className="profileActivityAction">{log.action || 'N/A'}</span>
                      </td>
                      <td>
                        {phoneTarget?.studentId ? (
                          <div className="activityLogDetail">
                            <span>Viewed phone for {phoneTarget.studentName}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary profileActivityBtn"
                              onClick={() => navigate('/view-student', { state: { myData: phoneTarget.studentId } })}
                            >
                              View Profile
                            </button>
                          </div>
                        ) : (
                          log.details || 'N/A'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileComp
